import { useEffect, useState, useRef } from "react";

/**
 * useSmartLoading — oculta estados de carga efímeros y evita parpadeos.
 *
 * Resuelve dos problemas del loading naïve:
 *   1. "Flash of loading state": si la respuesta llega en < 150ms, el loader
 *      aparece y desaparece tan rápido que se percibe como bug.
 *   2. Parpadeo: si el loader aparece y luego los datos llegan inmediatamente,
 *      el usuario ve el loader por 50ms → se siente brusco.
 *
 * La solución:
 *   - Espera `delay` ms antes de mostrar el loader (si la carga termina antes,
 *     el usuario nunca lo ve).
 *   - Una vez mostrado, lo mantiene visible al menos `minDuration` ms para
 *     evitar parpadeo aunque los datos ya hayan llegado.
 *
 * ── Uso ────────────────────────────────────────────────────────────────────
 *   function DevicesList() {
 *     const { devices, loading } = useDevices();
 *     const showLoader = useSmartLoading(loading);
 *
 *     if (showLoader) return <VehicleRowSkeleton count={6} />;
 *     return <List items={devices} />;
 *   }
 *
 *   // Con opciones custom:
 *   const showLoader = useSmartLoading(loading, {
 *     delay: 200,
 *     minDuration: 500,
 *   });
 *
 * ── Parámetros ─────────────────────────────────────────────────────────────
 * @param {boolean} loading
 *        Flag raw de carga (p. ej. el `loading` de un hook de fetch).
 * @param {Object} [options]
 * @param {number} [options.delay=150]
 *        Ms a esperar antes de mostrar el loader. Si `loading` vuelve a
 *        `false` antes, nunca se muestra. Defaults a 150ms (umbral bajo el
 *        cual los usuarios no perciben un loader como útil).
 * @param {number} [options.minDuration=300]
 *        Ms mínimos que el loader permanece visible una vez mostrado. Aunque
 *        `loading` ya sea `false`, se mantiene `true` hasta completar este
 *        tiempo. Evita parpadeo en respuestas justo arriba del umbral.
 *
 * @returns {boolean} `true` si se debe mostrar el loader.
 */
export default function useSmartLoading(
  loading,
  { delay = 150, minDuration = 300 } = {},
) {
  const [show, setShow] = useState(false);

  const shownAtRef = useRef(null);
  const showTimerRef = useRef(null);
  const hideTimerRef = useRef(null);

  useEffect(() => {
    const clearShow = () => {
      if (showTimerRef.current) {
        clearTimeout(showTimerRef.current);
        showTimerRef.current = null;
      }
    };
    const clearHide = () => {
      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current);
        hideTimerRef.current = null;
      }
    };

    if (loading) {
      clearHide();

      if (!show && !showTimerRef.current) {
        showTimerRef.current = setTimeout(() => {
          setShow(true);
          shownAtRef.current = Date.now();
          showTimerRef.current = null;
        }, delay);
      }
    } else {
      clearShow();

      if (show) {
        const elapsed = Date.now() - (shownAtRef.current ?? Date.now());
        const remaining = Math.max(0, minDuration - elapsed);

        hideTimerRef.current = setTimeout(() => {
          setShow(false);
          shownAtRef.current = null;
          hideTimerRef.current = null;
        }, remaining);
      }
    }

    return () => {
      clearShow();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, delay, minDuration]);

  useEffect(() => {
    return () => {
      if (showTimerRef.current) clearTimeout(showTimerRef.current);
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    };
  }, []);

  return show;
}
